<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="scrollElement" :class="containerStyles">
    <div
      v-if="title || list"
      class="flex flex-col col-start-2 col-span-12 lg:col-start-2 lg:col-span-6 mt-4 lg:mt-0"
    >
      <header2
        v-if="title"
        :text="title"
        :is-umbraco="isUmbraco"
        :theme="themeType"
        classes="mt-16 lg:mt-34"
      />
      <rich-text
        v-if="description"
        :text="description"
        :is-umbraco="isUmbraco"
        :theme="themeType"
        classes="mt-8"
      />
    </div>
    <div class="col-start-2 col-span-12 lg:col-start-3 lg:col-span-8">
      <list-item
        v-if="listItems.length != 0"
        v-for="list in listItems"
        :key="list.title"
        :title="list.title"
        :description="list.description"
        :list="list.list"
        :is-umbraco="isUmbraco"
        :theme="theme"
      ></list-item>
    </div>
  </section>
</template>

<script lang="ts" setup>
import {
  toRefs,
  computed,
  Ref,
  ref,
  onBeforeMount,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import { IListItem, IListProps } from "@/types/components/IListProps";
import { changeTheme, getThemeType } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import Header2 from "@components/Typography/Header2.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import ListItem from "@molecules/ListItem/ListItem.ce.vue";

const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);
const isLoading = ref(true);

const props = withDefaults(defineProps<IListProps>(), {
  isUmbraco: false,
});
const { title, description, theme, list, isUmbraco } = toRefs(props);

const onEnter = () => {
  changeTheme(theme.value);
};
const onExit = () => {};

onBeforeMount(() => {
  if (theme.value) {
    isLoading.value = false;
  }
});

onMounted(() => {
  if (!isLoading.value) {
    if (!scrollElement.value) return;
    observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
      threshold: 0.6,
    });
  }
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} sectionContainerShort place-content-start pb-20 lg:pb-34`;
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const listItems = computed(() => {
  if (!list.value) return [];
  const listsArray: IListItem[] = JSON.parse(list.value);
  const listItems = listsArray.map((listItem) => {
    return {
      title: listItem.title,
      description: listItem.description,
      list: listItem.list,
    };
  });
  return listItems;
});
</script>
